// Boot-time wiring for the commerce billing UI ports. These
// members are reached by non-settings callers — global modals, upgrade flows —
// so they must be live before any lazy settings screen renders. Imported at
// module scope in `_app.tsx`, right after `commerce-data-wiring`.
import { USAGE_POLICY } from '@/app-shell/app-root/legalDocuments';
import { DEFAULT_DATE_FORMAT } from '@/app-shell/constants';
import { useIsHeidi2OptInEnabled } from '@/app-shell/flags/agents-flags';
import {
  IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED,
  IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED,
  IS_HIDE_PRO_MONTHLY_OPTION,
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsHeidi2MemberUpgradeRequestEnabled,
  useIsHeidi2PracticeMaxUpgradeEnabled,
  useIsHeidi2PricingPlansModalEnabled,
  useIsHeidi2TeamUpgradesEnabled,
} from '@/app-shell/flags/commerce-flags';
import {
  IS_EVIDENCE_LAUNCH_BILLING_PAGE_REDESIGN_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
} from '@/app-shell/flags/evidence-flags';
import {
  useIsHeidi2ClinicianTrialEndEnabled,
  useIsHeidi2ClinicianTrialOfferEnabled,
  useIsHeidi2TrialEndEnabled,
  useIsHeidi2UsageNudgesEnabled,
} from '@/app-shell/flags/growth-flags';
import {
  IS_PRACTICE_INVITE_FLOW_ENABLED,
  useIsWorkspaceSettingsIaEnabled,
} from '@/app-shell/flags/workspace-flags';
import { useIsGamifyTrialExtensionEntryEnabled } from '@/app-shell/layout/Navigation/use-is-gamify-trial-extension-entry-enabled';
import { ClinicianUpgradeModal } from '@/app-shell/modals/clinician-modal/clinician-upgrade-modal';
import { TrialExtensionModalWrapper } from '@/app-shell/modals/trial-extension/TrialExtensionModalWrapper';
import { TrackedButton } from '@/app-shell/ui/tracked-button';
import walkthroughPoster from '@/assets/videos/evidence/walkthrough-poster.webp';
import {
  EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD,
  evidencePowerUserBannerDismissedAtom,
  evidencePowerUserDevForceShowBannerAtom,
} from '@/atoms/evidenceTrialQueryCountAtom';
import {
  dequeueModalAtom,
  enqueueModalAtom,
  seenModalIdsAtom,
} from '@/atoms/modalQueueAtoms';
import {
  clinicianMaxUpgradeModalEntryPointAtom,
  clinicianUpgradeModalOpenAtom,
  evidencePowerUserUpgradeModalOpenAtom,
  practiceUpgradeModalOpenAtom,
  trialModalPreviewDaysLeftAtom,
  upgradeModalFromTrialRecapAtom,
  usePaywallStates,
} from '@/atoms/paywallAtoms';
import { StackedSourceAvatars } from '@/components/ui/floating-ask-heidi/evidence-prompts-bar/stacked-source-avatars';
import { useQueryStripeLink } from '@/composition/hosts/commerce/settings/useQueryStripeLink';
import { useTrialRecapUsageStats } from '@/composition/hosts/commerce/trials-and-upgrades/useTrialRecapUsageStats';
import { useIsGamifyTrialArmAllowed } from '@/composition/hosts/growth/post-note-quality/hooks/use-is-gamify-trial-arm-allowed';
import { usePracticeInvite } from '@/composition/hosts/growth/practice-invite-team-members/usePracticeInvite';
import { useShowSessionsListAssistantView } from '@/composition/hosts/session/session-list/useShowSessionsListAssistantView';
import { getPrivacyPolicyLink } from '@/helpers/getPrivacyPolicyLink';
import { useRegion } from '@/helpers/getRegion';
import { useMutateApplySubscriptionDiscount } from '@/hooks/mutation/useMutateApplySubscriptionDiscount';
import { useMutateCheckoutV2 } from '@/hooks/mutation/useMutateCheckoutV2';
import { useMutateSubmitSalesBookingForm } from '@/hooks/mutation/useMutateSubmitSalesBookingForm';
import {
  invalidateGamifyTrialExtension,
  useQueryGamifyTrialExtension,
} from '@/hooks/query/useQueryGamifyTrialExtension';
import {
  formatPriceWithCurrency,
  getCurrencySymbol,
  getDisplayedPriceFromPlan,
  getNewProductTierProduct,
  getPlanFromPeriod,
  getProductByName,
  getProductUpgradeDisplayPrices,
  getTierProduct,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQuerySourceRecommendations } from '@/hooks/query/useQuerySourceRecommendations';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useEmailInviteForm } from '@/hooks/useEmailInviteForm';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useFeatureLists } from '@/hooks/useFeatureLists';
import { useIsEvidencePowerUserEnabled } from '@/hooks/useIsEvidencePowerUserEnabled';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { useShouldHideMarketRestrictedFeatures } from '@/hooks/useShouldHideMarketRestrictedFeatures';
import { useTrialExtensionModalHandlers } from '@/hooks/useTrialExtensionModalHandlers';
import { sleep } from '@/lib/utils';
import { isTauri, openUrlInTauri } from '@/tauri/utils';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isOnPrem } from '@/utils/buildProfile';
import { mapOrganisationSizeToHubSpot } from '@/utils/mapOrganisationSizeToHubSpot';
import { getStripeCallbackRedirect, getStripeReturnRedirect } from '@/utils/scribeHTTP';
import { useManualExposure } from '@heidi/analytics/events';
import {
  AppleIapInfoText,
  configureCommerceBillingUi,
  configureCommerceTrialsUi,
  getCurrentPlanNameForTrial,
  HeidiUpgradeBaseModal,
  PriceDisplay,
  TrialStartModalBase,
  useQueryCreditAllowance,
  useUpgradeModalTrialText,
  useUserQuota,
  type CommerceBillingUiPorts,
  type CommerceTrialsUiPorts,
} from '@heidi/commerce';
import {
  BILLING_PLANS_PRICING_URL,
  FREE_VS_PRO_PLANS_ACCOUNT_ANCHOR_HELP_ARTICLE_PATH,
  GET_IN_TOUCH_URL,
  getBillingHelpCentreUrl,
  getBillingPlansPricingUrl,
  getComparePlansUrl,
  getGetInTouchUrl,
  getUsageLimitsHelpUrl,
} from '@heidi/commerce/data';
import { useIsEvidenceQueryLimitsForLegacyTiersEnabled } from '@heidi/evidence/flags';
import { normalizeCountryName, usePracticeInviteEmailForm } from '@heidi/growth';
import {
  getHubSpotTrackingCookie,
  inviteTeamStepCompletedAtom,
  practiceTrialModalPendingAtom,
} from '@heidi/growth/data';
import { UserActivityAction } from '@heidi/schema/ml-scribe';
import { ExternalLink } from '@heidi/snowflake-ui/external-link';
import { Loader } from '@heidi/snowflake-ui/loader';
import { PhoneInput } from '@heidi/snowflake-ui/phone-input';
import { getTermsOfUseLink } from '@heidi/templates/data';
import {
  useMutateMarkModalAsViewed,
  useUserActivityActionCount,
} from '@heidi/workspace/data';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { createElement, lazy, type ComponentType } from 'react';

// The thunk is not called here, so the imported binding stays unread until use.
function callThrough<T extends (...args: never[]) => unknown>(read: () => T): T {
  return ((...args: Parameters<T>) => read()(...args)) as T;
}

function componentThrough<P extends object>(
  read: () => ComponentType<P>
): ComponentType<P> {
  return function TrialsPortView(props) {
    return createElement(read(), props);
  };
}

const useUserQuotaHost = () =>
  useUserQuota(
    useQueryUser as CommerceBillingUiPorts['useQueryUser'],
    DEFAULT_DATE_FORMAT
  );

configureCommerceBillingUi({
  AppleIapInfoText:
    AppleIapInfoText as unknown as CommerceBillingUiPorts['AppleIapInfoText'],
  ExternalLink,
  HeidiUpgradeBaseModal:
    HeidiUpgradeBaseModal as unknown as CommerceBillingUiPorts['HeidiUpgradeBaseModal'],
  PriceDisplay: PriceDisplay as unknown as CommerceBillingUiPorts['PriceDisplay'],
  TrackedButton: TrackedButton as unknown as CommerceBillingUiPorts['TrackedButton'],
  ClinicianUpgradeModal,
  usePracticeUpgradeModalState: () => useAtom(practiceUpgradeModalOpenAtom),
  useSetPracticeUpgradeModalOpen: () => useSetAtom(practiceUpgradeModalOpenAtom),
  useUpgradeModalFromTrialRecapState: () => useAtom(upgradeModalFromTrialRecapAtom),
  useTrialModalPreviewDaysLeftValue: () => useAtomValue(trialModalPreviewDaysLeftAtom),
  useSetTrialModalPreviewDaysLeft: () => useSetAtom(trialModalPreviewDaysLeftAtom),
  useSetClinicianMaxUpgradeModalEntryPoint: () =>
    useSetAtom(clinicianMaxUpgradeModalEntryPointAtom) as unknown as (
      entryPoint: string | null
    ) => void,
  useSetClinicianUpgradeModalOpen: () => useSetAtom(clinicianUpgradeModalOpenAtom),
  usePaywallStates,
  isBillingCustomDowngradeFlowsEnabled: () =>
    IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED() === true,
  isHideProMonthlyOption: () => IS_HIDE_PRO_MONTHLY_OPTION() === true,
  isEndOf90DayTrialExperienceEnabled: () =>
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() === true,
  isEvidenceLaunchTrialAndUpgradeFlowsEnabled: () =>
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() === true,
  isEvidenceLaunchBillingPageRedesignEnabled: () =>
    IS_EVIDENCE_LAUNCH_BILLING_PAGE_REDESIGN_ENABLED() === true,
  useIsHeidi2PricingPlansModalEnabled,
  useIsHeidi2TeamUpgradesEnabled,
  useIsHeidi2MemberUpgradeRequestEnabled,
  useIsHeidi2PracticeMaxUpgradeEnabled,
  useIsHeidi2OptInEnabled,
  useIsHeidi2TrialEndEnabled,
  useIsHeidi2ClinicianTrialEndEnabled,
  useIsHeidi2ClinicianTrialOfferEnabled,
  useIsHeidi2UsageNudgesEnabled,
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsEvidenceQueryLimitsForLegacyTiersEnabled,
  useIsWorkspaceSettingsIaEnabled,
  useIsGamifyTrialExtensionEntryEnabled,
  useIsGamifyTrialArmAllowed,
  USAGE_POLICY,
  billingUrls: {
    getUsageLimitsHelpUrl,
    getBillingHelpCentreUrl,
    getBillingPlansPricingUrl,
    BILLING_PLANS_PRICING_URL,
    getGetInTouchUrl,
    GET_IN_TOUCH_URL,
    getComparePlansUrl,
    FREE_VS_PRO_PLANS_ACCOUNT_ANCHOR_HELP_ARTICLE_PATH,
  },
  getTermsOfUseLink,
  useMutateApplySubscriptionDiscount:
    useMutateApplySubscriptionDiscount as unknown as CommerceBillingUiPorts['useMutateApplySubscriptionDiscount'],
  useMutateCheckoutV2:
    useMutateCheckoutV2 as unknown as CommerceBillingUiPorts['useMutateCheckoutV2'],
  useQueryCreditAllowance:
    useQueryCreditAllowance as unknown as CommerceBillingUiPorts['useQueryCreditAllowance'],
  useQueryGamifyTrialExtension:
    useQueryGamifyTrialExtension as unknown as CommerceBillingUiPorts['useQueryGamifyTrialExtension'],
  products: {
    useQueryProducts,
    formatPriceWithCurrency,
    getCurrencySymbol,
    getDisplayedPriceFromPlan,
    getNewProductTierProduct,
    getPlanFromPeriod,
    getProductByName,
    getProductUpgradeDisplayPrices,
    getTierProduct,
  } as unknown as CommerceBillingUiPorts['products'],
  useQueryStripeLink:
    useQueryStripeLink as unknown as CommerceBillingUiPorts['useQueryStripeLink'],
  useQueryUser: useQueryUser as CommerceBillingUiPorts['useQueryUser'],
  useEvidenceBillingInfo:
    useEvidenceBillingInfo as unknown as CommerceBillingUiPorts['useEvidenceBillingInfo'],
  useFeatureLists,
  useShouldHideMarketRestrictedFeatures,
  useUpgradeModalTrialText:
    useUpgradeModalTrialText as unknown as CommerceBillingUiPorts['useUpgradeModalTrialText'],
  getCurrentPlanNameForTrial,
  useUserQuota: useUserQuotaHost as unknown as CommerceBillingUiPorts['useUserQuota'],
  useRegion,
  isAppleCompliantBuild,
  isOnPrem,
  isTauri,
  openUrlInTauri: (url: string) => {
    void openUrlInTauri(url);
  },
  useAppRouter: useRouter,
  useManualExposure:
    useManualExposure as unknown as CommerceBillingUiPorts['useManualExposure'],
  sleep,
  getStripeCallbackRedirect,
  getStripeReturnRedirect,
});

// Trials ports share this boot module so both registries exist before any
// lazy modal renders. Every member is a call-through; none is invoked here.
const InputEmails = lazy(() =>
  import('@heidi/snowflake-ui/input-emails').then((mod) => ({ default: mod.InputEmails }))
);

configureCommerceTrialsUi({
  StackedSourceAvatars: componentThrough(
    () => StackedSourceAvatars as unknown as CommerceTrialsUiPorts['StackedSourceAvatars']
  ),
  TrialStartModalBase: componentThrough(
    () => TrialStartModalBase as unknown as CommerceTrialsUiPorts['TrialStartModalBase']
  ),
  Loader: componentThrough(() => Loader as unknown as CommerceTrialsUiPorts['Loader']),
  PhoneInput: componentThrough(
    () => PhoneInput as unknown as CommerceTrialsUiPorts['PhoneInput']
  ),
  Head: componentThrough(() => Head as unknown as CommerceTrialsUiPorts['Head']),
  InputEmails: InputEmails as unknown as CommerceTrialsUiPorts['InputEmails'],
  walkthroughPosterSrc: walkthroughPoster,
  useEnqueueModal: () => {
    const enqueue = useSetAtom(enqueueModalAtom);
    return (modal) => {
      enqueue(modal as Parameters<typeof enqueue>[0]);
    };
  },
  useDequeueModal: () => useSetAtom(dequeueModalAtom),
  useSeenModalIds: () => useAtom(seenModalIdsAtom),
  useQueuedModal: callThrough(
    () => useQueuedModal as unknown as CommerceTrialsUiPorts['useQueuedModal']
  ),
  useInviteTeamStepCompleted: () => useAtomValue(inviteTeamStepCompletedAtom),
  useSetPracticeTrialModalPending: () => useSetAtom(practiceTrialModalPendingAtom),
  evidencePowerUserBannerCountThreshold: EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD,
  useEvidencePowerUserBannerDismissedState: () =>
    useAtom(evidencePowerUserBannerDismissedAtom),
  useEvidencePowerUserDevForceShowBannerState: () =>
    useAtom(evidencePowerUserDevForceShowBannerAtom),
  useEvidencePowerUserUpgradeModalOpenState: () =>
    useAtom(evidencePowerUserUpgradeModalOpenAtom),
  useEvidenceTrialQueryCountState: () =>
    useUserActivityActionCount(UserActivityAction.evidence_chat_query),
  useSetEvidencePowerUserUpgradeModalOpen: () =>
    useSetAtom(evidencePowerUserUpgradeModalOpenAtom),
  useEvidenceAnalytics: callThrough(
    () => useEvidenceAnalytics as unknown as CommerceTrialsUiPorts['useEvidenceAnalytics']
  ),
  useIsEvidencePowerUserEnabled: callThrough(() => useIsEvidencePowerUserEnabled),
  TrialExtensionModalWrapper,
  useEmailInviteForm: callThrough(() => useEmailInviteForm),
  invalidateGamifyTrialExtension,
  useTrialExtensionModalHandlers: callThrough(
    () =>
      useTrialExtensionModalHandlers as unknown as CommerceTrialsUiPorts['useTrialExtensionModalHandlers']
  ),
  useEvidenceAccess: callThrough(
    () => useEvidenceAccess as unknown as CommerceTrialsUiPorts['useEvidenceAccess']
  ),
  useQuerySourceRecommendations: callThrough(
    () =>
      useQuerySourceRecommendations as unknown as CommerceTrialsUiPorts['useQuerySourceRecommendations']
  ),
  useShowSessionsListAssistantView: callThrough(() => useShowSessionsListAssistantView),
  useTrialRecapUsageStats: callThrough(
    () =>
      useTrialRecapUsageStats as unknown as CommerceTrialsUiPorts['useTrialRecapUsageStats']
  ),
  usePracticeInvite: () => usePracticeInvite(),
  usePracticeInviteEmailForm: callThrough(
    () =>
      usePracticeInviteEmailForm as unknown as CommerceTrialsUiPorts['usePracticeInviteEmailForm']
  ),
  useQueryUserTier: callThrough(
    () => useQueryUserTier as unknown as CommerceTrialsUiPorts['useQueryUserTier']
  ),
  useMutateMarkModalAsViewed: () => {
    const { mutate } = useMutateMarkModalAsViewed();
    return {
      mutate:
        mutate as CommerceTrialsUiPorts['useMutateMarkModalAsViewed'] extends () => {
          mutate: infer Mutate;
        }
          ? Mutate
          : never,
    };
  },
  useMutateSubmitSalesBookingForm: () => {
    const { mutateAsync, isPending } = useMutateSubmitSalesBookingForm();
    return { mutateAsync, isPending: isPending ?? false };
  },
  isPracticeInviteFlowEnabled: () => IS_PRACTICE_INVITE_FLOW_ENABLED() === true,
  getPrivacyPolicyLink: callThrough(() => getPrivacyPolicyLink),
  getHubSpotTrackingCookie: callThrough(() => getHubSpotTrackingCookie),
  mapOrganisationSizeToHubSpot: callThrough(() => mapOrganisationSizeToHubSpot),
  normalizeCountryName: callThrough(() => normalizeCountryName),
});
